/**
 * Modal que se muestra cuando el usuario intenta vincular un dispositivo
 * que ya está vinculado a otra cuenta
 * Sprint 3 - Device Bind Flow
 * Sprint 9 - Permite solicitar desvinculación desde el mismo modal
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Alert, Input } from '../ui';
import { otpService, savePendingEmail } from '../../services/otp';

interface DeviceAlreadyBoundModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function DeviceAlreadyBoundModal({ isOpen, onClose }: DeviceAlreadyBoundModalProps) {
    const navigate = useNavigate();
    const [showUnbindForm, setShowUnbindForm] = useState(false);
    const [accountEmail, setAccountEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        // No prellenamos el correo porque suele ser la cuenta nueva (sin vínculo)
        // y lleva al error NO_DEVICE_BOUND. El usuario debe capturar la cuenta
        // que actualmente tiene el dispositivo vinculado.
        setAccountEmail('');
        setPassword('');
        setShowUnbindForm(false);
        setError(null);
    }, [isOpen]);

    const handleClose = useCallback(() => {
        setShowUnbindForm(false);
        setPassword('');
        setError(null);
        onClose();
    }, [onClose]);

    const handleRequestUnbind = useCallback(async () => {
        const normalizedEmail = accountEmail.trim().toLowerCase();

        if (!normalizedEmail || !password) {
            setError('Ingresa correo y contraseña para continuar');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await otpService.requestUnbind(normalizedEmail, password);
            savePendingEmail(normalizedEmail);
            handleClose();
            navigate('/device-unbind', { state: { email: normalizedEmail } });
        } catch (err: unknown) {
            const apiError = err as { error_code?: string; message?: string };

            if (apiError.error_code === 'SAME_DEVICE') {
                setError('Ese correo ya está usando este mismo dispositivo.');
            } else if (apiError.error_code === 'NO_DEVICE_BOUND') {
                setError('Ese correo no tiene un dispositivo vinculado actualmente.');
            } else {
                setError(apiError.message || 'Error al solicitar desvinculación');
            }
        } finally {
            setIsLoading(false);
        }
    }, [accountEmail, password, navigate, handleClose]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Dispositivo ya vinculado"
            closeOnBackdrop={false}
            closeOnEscape={false}
        >
            <div className="flex flex-col gap-6">
                <Alert type="warning">
                    Este dispositivo ya está vinculado a otra cuenta.
                </Alert>

                {!showUnbindForm && (
                    <>
                        <div className="flex flex-col gap-4 text-[0.95rem] leading-6 text-text-secondary">
                            <p>
                                Por razones de seguridad, cada dispositivo solo puede estar
                                vinculado a una cuenta a la vez.
                            </p>
                            <p>
                                Si deseas usar este dispositivo con otra cuenta, primero
                                debes desvincularlo desde la cuenta actualmente vinculada.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 pt-2">
                            <Button
                                variant="primary"
                                onClick={() => setShowUnbindForm(true)}
                                fullWidth
                            >
                                Solicitar desvinculación
                            </Button>

                            <Button
                                variant="secondary"
                                onClick={() => {
                                    window.location.href = 'mailto:soporte@marketplace.com?subject=Ayuda%20con%20desvinculaci%C3%B3n%20de%20dispositivo';
                                }}
                                fullWidth
                            >
                                Contactar soporte
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={handleClose}
                                fullWidth
                            >
                                Entendido
                            </Button>
                        </div>
                    </>
                )}

                {showUnbindForm && (
                    <>
                        <div className="flex flex-col gap-4 text-[0.95rem] leading-6 text-text-secondary">
                            <p>
                                Ingresa el correo y contraseña de la cuenta que tiene
                                este dispositivo vinculado. Te enviaremos un código OTP
                                para completar la desvinculación.
                            </p>
                            <p className="text-xs text-text-muted">
                                Si ves "Ese correo no tiene un dispositivo vinculado",
                                estás usando una cuenta distinta. Prueba con la cuenta
                                que vinculó este dispositivo por primera vez.
                            </p>
                        </div>

                        {error && (
                            <Alert type="error">
                                {error}
                            </Alert>
                        )}

                        <div className="flex flex-col gap-3">
                            <Input
                                label="Correo de la cuenta vinculada"
                                type="email"
                                value={accountEmail}
                                onChange={(e) => setAccountEmail(e.target.value)}
                                placeholder="correo@ejemplo.com"
                                autoComplete="email"
                                autoCapitalize="none"
                                inputMode="email"
                                disabled={isLoading}
                            />

                            <Input
                                label="Contraseña"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Tu contraseña"
                                autoComplete="current-password"
                                disabled={isLoading}
                            />
                        </div>

                        <div className="flex flex-col gap-3 pt-2">
                            <Button
                                variant="primary"
                                onClick={handleRequestUnbind}
                                isLoading={isLoading}
                                disabled={!accountEmail.trim() || !password}
                                fullWidth
                            >
                                Enviar código de verificación
                            </Button>

                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setShowUnbindForm(false);
                                    setPassword('');
                                    setError(null);
                                }}
                                disabled={isLoading}
                                fullWidth
                            >
                                Volver
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
